import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import {
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarFooter,
  useSidebar,
} from './ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Link } from './ui/link';
import { Mail, Power, CreditCard } from 'lucide-react';
import { cn } from '../lib/utils';
import { UserFeedbackForm } from './UserFeedbackForm';
import { OnboardingTrophy } from './OnboardingTrophy';
import { UserAvatar } from './UserAvatar';

type UserAvatarMenuProps = {
  variant?: 'sidebar' | 'header';
};

export const UserAvatarMenu: React.FC<UserAvatarMenuProps> = ({ variant = 'sidebar' }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useLocale();
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { state, isMobile, openMobile, setOpenMobile } = useSidebar();
  const isCollapsed = isMobile ? !openMobile : state === 'collapsed';
  const isHeader = variant === 'header';

  const handleMenuClick = (): void => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const handleLogout = (): void => {
    logout();
    navigate('/login');
  };

  const avatar = <UserAvatar email={user?.email} />;

  const menu = (
    <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
      <DropdownMenuTrigger asChild>
        {isHeader ? (
          <button
            type="button"
            className="inline-flex shrink-0 items-center justify-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={user?.email || t('user_settings') || 'User'}
          >
            {avatar}
          </button>
        ) : (
          <SidebarMenuButton className={cn('w-full', isCollapsed && 'justify-center')} tooltip={user?.email || 'User'}>
            {avatar}
            {!isCollapsed && (
              <div className="flex flex-col items-start flex-1 min-w-0">
                <span className="text-sm font-medium truncate w-full">
                  {user?.email?.split('@')[0] || 'User'}
                </span>
                <span className="text-xs text-muted-foreground truncate w-full">
                  {user?.email || ''}
                </span>
              </div>
            )}
          </SidebarMenuButton>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {user?.admin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link
                to="/admin"
                onClick={(e) => {
                  if (e.ctrlKey || e.metaKey) return;
                  e.preventDefault();
                  setIsDropdownOpen(false);
                  handleMenuClick();
                  navigate('/admin');
                }}
              >
                {t('admin_panel')}
              </Link>
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link
            to="/desk/usage-and-billing"
            onClick={(e) => {
              if (e.ctrlKey || e.metaKey) return;
              e.preventDefault();
              setIsDropdownOpen(false);
              handleMenuClick();
              navigate('/desk/usage-and-billing');
            }}
          >
            <CreditCard className="h-4 w-4 mr-2" />
            {t('usage_and_billing')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => {
          setIsDropdownOpen(false);
          setIsFeedbackModalOpen(true);
        }}>
          <Mail className="h-4 w-4 mr-2" />
          {t('Feedback')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => {
          setIsDropdownOpen(false);
          handleLogout();
        }}>
          <Power className="h-4 w-4 mr-2" />
          {t('logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <>
      {isHeader ? (
        menu
      ) : (
        <SidebarFooter>
          <OnboardingTrophy />
          <SidebarMenu>
            <SidebarMenuItem>
              {menu}
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      )}
      <Dialog
        open={isFeedbackModalOpen}
        onOpenChange={(open) => setIsFeedbackModalOpen(open)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('report_feedback')}</DialogTitle>
            <DialogDescription>{t('feedback_description')}</DialogDescription>
          </DialogHeader>
          <UserFeedbackForm
            onClose={() => setIsFeedbackModalOpen(false)}
            onSubmitted={() => setIsFeedbackModalOpen(false)}
            showCancelButton={true}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};
